/**
 * Retry Utility
 *
 * Provides exponential backoff retry logic for async operations.
 * Used by adapters to handle transient network/server failures.
 */

/**
 * Configuration options for retry behavior
 */
export interface RetryOptions {
  /** Maximum number of attempts (including initial). Default: 3 */
  maxAttempts?: number;
  /** Initial delay in ms before first retry. Default: 100 */
  initialDelayMs?: number;
  /** Multiplier for exponential backoff. Default: 2 */
  backoffMultiplier?: number;
  /** Maximum delay cap in ms. Default: 2000 */
  maxDelayMs?: number;
  /** Custom function to determine if error is retryable */
  shouldRetry?: (error: unknown) => boolean;
}

const DEFAULT_OPTIONS: Required<Omit<RetryOptions, 'shouldRetry'>> = {
  maxAttempts: 3,
  initialDelayMs: 100,
  backoffMultiplier: 2,
  maxDelayMs: 2000,
};

/**
 * Check if an error is retryable (network error or 5xx server error)
 */
export function isRetryableError(error: unknown): boolean {
  // Network errors from fetch (TypeError with specific messages)
  if (error instanceof TypeError) {
    const message = error.message.toLowerCase();
    if (
      message.includes('fetch') ||
      message.includes('network') ||
      message.includes('failed to fetch')
    ) {
      return true;
    }
  }

  // Error with status code (5xx)
  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status: number }).status;
    if (typeof status === 'number' && status >= 500 && status < 600) {
      return true;
    }
  }

  // Check error message for common network issues
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    if (
      message.includes('network') ||
      message.includes('timeout') ||
      message.includes('econnrefused') ||
      message.includes('econnreset') ||
      message.includes('socket hang up') ||
      message.includes('server error')
    ) {
      return true;
    }
  }

  return false;
}

/**
 * Sleep for specified milliseconds
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Calculate delay for a given attempt (0-indexed)
 */
function calculateDelay(
  attempt: number,
  initialDelayMs: number,
  backoffMultiplier: number,
  maxDelayMs: number
): number {
  const delay = initialDelayMs * Math.pow(backoffMultiplier, attempt);
  return Math.min(delay, maxDelayMs);
}

/**
 * Execute an async function with retry logic and exponential backoff.
 *
 * @param fn - Async function to execute
 * @param options - Retry configuration
 * @returns Result of fn() on success
 * @throws Last error after all retries exhausted, or immediately for non-retryable errors
 *
 * @example
 * ```typescript
 * // Basic usage
 * const response = await withRetry(() => fetch(url));
 *
 * // With custom options
 * const response = await withRetry(
 *   () => fetch(url),
 *   { maxAttempts: 5, initialDelayMs: 200 }
 * );
 *
 * // Throw on 5xx to trigger retry
 * const response = await withRetry(async () => {
 *   const res = await fetch(url);
 *   if (res.status >= 500) {
 *     throw Object.assign(new Error(`Server error: ${res.status}`), { status: res.status });
 *   }
 *   return res;
 * });
 * ```
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxAttempts = DEFAULT_OPTIONS.maxAttempts,
    initialDelayMs = DEFAULT_OPTIONS.initialDelayMs,
    backoffMultiplier = DEFAULT_OPTIONS.backoffMultiplier,
    maxDelayMs = DEFAULT_OPTIONS.maxDelayMs,
    shouldRetry = isRetryableError,
  } = options;

  let lastError: unknown;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // Check if we should retry
      const isLastAttempt = attempt === maxAttempts - 1;
      const canRetry = shouldRetry(error);

      if (isLastAttempt || !canRetry) {
        throw error;
      }

      // Wait before next attempt
      const delay = calculateDelay(attempt, initialDelayMs, backoffMultiplier, maxDelayMs);
      await sleep(delay);
    }
  }

  // Should never reach here, but TypeScript needs this
  throw lastError;
}
